import React from 'react';
import { useTranslation } from 'react-i18next';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';

/**
 * Page controls — Figma node 16370:190241, the footer of the "Exam History"
 * table in the same "Course pages" section.
 *
 * The subscriptions frame draws no pager of its own; it draws eight cards and
 * stops. This is the section's one pager, taken as it is: a line saying how
 * many are shown at the inline start, and at the end 32px squares on a 6px
 * radius with gray/300 hairlines, the current page filled in the brand blue.
 *
 * DIRECTION
 *
 * The design lays the squares out left to right — ‹ 1 2 3 › — inside an
 * Arabic page. Written here in the order the Arabic reader meets them instead:
 * "previous" first, then page one, so page one sits at the right beside the
 * arrow back and the numbers climb leftward, which is how an Arabic pager
 * reads; English mirrors it for free. The arrows point outward, as the deck's
 * do, so their glyphs flip under `rtl:` and neither button moves.
 *
 * At the ends the arrow is disabled rather than wrapping. The deck wraps
 * because it holds a handful of cards; a pager that leaps from the last page
 * to the first would lose the reader's place in a long list.
 *
 * A long run of pages is cut to the first, the last and the neighbours of the
 * current one, with "…" in the gaps. Nothing is drawn at all for one page.
 */
const SQUARE =
    'tw-flex tw-size-[32px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[6px] ' +
    'tw-text-[12px] tw-font-bold tw-leading-[1.5] tw-transition-colors ' +
    'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-v2-brand focus-visible:tw-ring-offset-2';

const IDLE =
    'tw-bg-white tw-border tw-border-solid tw-border-v2-ground-400 tw-text-v2-student-name ' +
    'hover:tw-bg-v2-neutral-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-40 disabled:hover:tw-bg-white';

const CURRENT = 'tw-bg-v2-brand tw-text-white';

/** The page numbers to draw, with `null` where a run is skipped. */
const pagesToShow = (page, pageCount) => {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);

    const shown = new Set([1, pageCount, page - 1, page, page + 1]);
    if (page <= 3) [2, 3, 4].forEach((p) => shown.add(p));
    if (page >= pageCount - 2) [pageCount - 3, pageCount - 2, pageCount - 1].forEach((p) => shown.add(p));

    const sorted = [...shown].filter((p) => p >= 1 && p <= pageCount).sort((a, b) => a - b);

    return sorted.flatMap((p, i) => (i > 0 && p - sorted[i - 1] > 1 ? [null, p] : [p]));
};

const Pagination = ({ page, pageCount, summary, onChange }) => {
    const { t } = useTranslation();

    if (pageCount <= 1) return null;

    return (
        <nav
            aria-label={t('v2.student.pagination.label')}
            className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-[16px]"
        >
            <p className="tw-m-0 tw-text-start tw-text-[14px] tw-leading-[1.6] tw-text-v2-student-nav">
                {summary}
            </p>

            <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-items-center tw-gap-[8px] tw-p-0">
                <li>
                    <button
                        type="button"
                        onClick={() => onChange(page - 1)}
                        disabled={page === 1}
                        aria-label={t('v2.student.deck.previous')}
                        className={`${SQUARE} ${IDLE}`}
                    >
                        <LuChevronLeft size={14} aria-hidden className="rtl:tw-rotate-180" />
                    </button>
                </li>

                {pagesToShow(page, pageCount).map((item, index) => (
                    item === null ? (
                        <li
                            // eslint-disable-next-line react/no-array-index-key
                            key={`gap-${index}`}
                            aria-hidden
                            className="tw-px-[2px] tw-text-[12px] tw-text-v2-student-nav"
                        >
                            …
                        </li>
                    ) : (
                        <li key={item}>
                            <button
                                type="button"
                                onClick={() => onChange(item)}
                                aria-current={item === page ? 'page' : undefined}
                                aria-label={t('v2.student.pagination.page', { page: item })}
                                className={`${SQUARE} ${item === page ? CURRENT : IDLE}`}
                            >
                                {item}
                            </button>
                        </li>
                    )
                ))}

                <li>
                    <button
                        type="button"
                        onClick={() => onChange(page + 1)}
                        disabled={page === pageCount}
                        aria-label={t('v2.student.deck.next')}
                        className={`${SQUARE} ${IDLE}`}
                    >
                        <LuChevronRight size={14} aria-hidden className="rtl:tw-rotate-180" />
                    </button>
                </li>
            </ul>
        </nav>
    );
};

export default Pagination;
